<template>
    <div class="mt-4">
        <h5 class="font-weight-bolder">Payment Gateway</h5>
        <p class="text-muted">
            Used to collect fee payments online. Once saved, secret values are never shown again here -
            leave a secret field blank to keep its current value unchanged.
        </p>

        <span v-if="loading" class="text-warning">Loading settings...</span>

        <div v-else>
            <b-form-group label="Provider">
                <b-form-select v-model="form.provider" :options="providerOptions" @change="onProviderChange" />
                <small class="text-muted">
                    Only one provider collects payments at a time - enabling this one disables the other.
                </small>
            </b-form-group>

            <b-form-group>
                <b-form-checkbox v-model="form.is_active" inline>
                    Enabled
                </b-form-checkbox>
            </b-form-group>

            <!-- HDFC SmartGateway (Juspay) -->
            <template v-if="form.provider === 'hdfc'">
                <b-form-group label="API Key">
                    <b-form-input v-model.trim="form.api_key" type="password"
                        :placeholder="status.has_api_key ? 'Already set - leave blank to keep it' : 'Enter API key'" />
                </b-form-group>

                <b-form-group label="Merchant ID">
                    <b-form-input v-model.trim="form.merchant_id" type="password"
                        :placeholder="status.has_merchant_id ? 'Already set - leave blank to keep it' : 'Enter merchant ID'" />
                </b-form-group>

                <b-form-group label="Base URL">
                    <b-form-input v-model.trim="form.base_url" placeholder="e.g. https://smartgatewayuat.hdfcbank.com" />
                </b-form-group>

                <b-form-group label="Payment Page Client ID">
                    <b-form-input v-model.trim="form.payment_page_client_id" />
                </b-form-group>

                <hr>
                <h6 class="font-weight-bold">Webhook</h6>
                <p class="text-muted small">
                    Configure these same values in the gateway's own merchant dashboard, under webhook Basic Auth.
                </p>

                <b-form-group label="Webhook Username">
                    <b-form-input v-model.trim="form.webhook_username" />
                </b-form-group>

                <b-form-group label="Webhook Password">
                    <b-form-input v-model.trim="form.webhook_password" type="password"
                        :placeholder="status.has_webhook_password ? 'Already set - leave blank to keep it' : 'Enter webhook password'" />
                </b-form-group>
            </template>

            <!-- HDFC Razorpay -->
            <template v-else-if="form.provider === 'razorpay'">
                <!-- Razorpay has no separate test URL: the key prefix alone decides
                     whether real money moves, so it is surfaced prominently. -->
                <b-alert v-if="status.key_mode === 'test'" show variant="warning" class="py-2">
                    <strong>Test mode.</strong> The saved Key ID starts with <code>rzp_test_</code>, so no real
                    money will be collected. Use an <code>rzp_live_</code> key to go live.
                </b-alert>
                <b-alert v-else-if="status.key_mode === 'live'" show variant="success" class="py-2">
                    <strong>Live mode.</strong> The saved Key ID starts with <code>rzp_live_</code> - real
                    payments will be collected.
                </b-alert>

                <!-- Left as plain text, unlike the secrets: the Key ID is not
                     confidential and seeing the prefix is how a test key gets
                     caught before go-live. -->
                <b-form-group label="Key ID">
                    <b-form-input v-model.trim="form.api_key"
                        :placeholder="status.has_api_key ? 'Already set - leave blank to keep it' : 'rzp_live_xxxxxxxx or rzp_test_xxxxxxxx'" />
                </b-form-group>

                <b-form-group label="Key Secret">
                    <b-form-input v-model.trim="form.api_secret" type="password"
                        :placeholder="status.has_api_secret ? 'Already set - leave blank to keep it' : 'Enter key secret'" />
                </b-form-group>

                <hr>
                <h6 class="font-weight-bold">Webhook</h6>
                <p class="text-muted small">
                    In the Razorpay dashboard, add a webhook with the URL below, subscribe it to the
                    <code>order.paid</code> event, and paste the secret you set there into the field below.
                    Webhooks are configured separately for Test and Live mode.
                </p>

                <b-form-group label="Webhook URL">
                    <b-form-input :value="razorpayWebhookUrl" readonly @focus="$event.target.select()" />
                </b-form-group>

                <b-form-group label="Webhook Secret">
                    <b-form-input v-model.trim="form.webhook_secret" type="password"
                        :placeholder="status.has_webhook_secret ? 'Already set - leave blank to keep it' : 'Enter webhook secret'" />
                </b-form-group>
            </template>

            <b-button variant="primary" :disabled="saving" @click="save">
                Save
            </b-button>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            loading: false,
            saving: false,
            providerOptions: [
                { value: 'hdfc', text: 'HDFC SmartGateway' },
                { value: 'razorpay', text: 'HDFC Razorpay' },
            ],
            status: {
                configured: false,
                has_api_key: false,
                has_api_secret: false,
                has_merchant_id: false,
                has_webhook_password: false,
                has_webhook_secret: false,
                key_mode: null,
            },
            form: {
                provider: 'hdfc',
                is_active: true,
                api_key: '',
                api_secret: '',
                merchant_id: '',
                base_url: '',
                payment_page_client_id: '',
                webhook_username: '',
                webhook_password: '',
                webhook_secret: '',
            },
        };
    },

    computed: {
        /**
         * mainAPI already carries the /api/v1 prefix and the URL salt, so this
         * is the exact path to paste into the Razorpay dashboard.
         */
        razorpayWebhookUrl() {
            return `${this.$store.getters.getAPIKey.mainAPI}/payments/payment/webhook/razorpay`;
        },
    },

    created() {
        this.loadSettings();
    },

    methods: {
        /**
         * Called without an argument on mount, where the backend returns
         * whichever provider the institution actually has configured; called
         * with one when the admin switches provider in the dropdown, to show
         * that provider's own saved values.
         */
        async loadSettings(provider = null) {
            this.loading = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/paymentgateway`;
                const response = await this.$axios.get(url, provider ? { params: { provider } } : {});

                this.status = response.data;

                // On mount the backend decides which provider to show; keep the
                // dropdown in step with it. Falls back to the current selection
                // when nothing is configured yet.
                this.form.provider = response.data.provider || provider || this.form.provider;
                this.form.is_active = response.data.is_active;
                this.form.base_url = response.data.base_url || '';
                this.form.payment_page_client_id = response.data.payment_page_client_id || '';
                this.form.webhook_username = response.data.webhook_username || '';
            } catch (error) {
                this.$toast.error('Failed to load payment gateway settings', {
                    position: 'top',
                    duration: 4000,
                });
            } finally {
                this.loading = false;
            }
        },

        /**
         * b-form-select fires @change with the newly selected value before the
         * v-model binding is visible here, so it is passed through explicitly.
         * Secrets are cleared so a value typed for one provider can never be
         * submitted against another's credentials.
         */
        onProviderChange(provider) {
            this.clearSecretInputs();
            this.loadSettings(provider);
        },

        clearSecretInputs() {
            this.form.api_key = '';
            this.form.api_secret = '';
            this.form.merchant_id = '';
            this.form.webhook_password = '';
            this.form.webhook_secret = '';
        },

        async save() {
            this.saving = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/paymentgateway`;
                await this.$axios.post(url, this.form);

                this.$toast.success('Payment gateway settings saved successfully.', {
                    position: 'top',
                    duration: 4000,
                });

                // Secrets are write-only - clear the inputs and refresh the
                // has_* flags rather than leaving what was just typed on screen.
                this.clearSecretInputs();
                await this.loadSettings(this.form.provider);
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to save payment gateway settings.', {
                    position: 'top',
                    duration: 4000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
